// =============================================================================
// Case Management Types
// =============================================================================

// Status and enum types
export type CaseStatus = 'collection' | 'analysis' | 'review' | 'reporting' | 'closed' | 'archived'

export type CasePriority = 'low' | 'medium' | 'high' | 'critical'

export type CaseTeamRole = 'lead' | 'analyst' | 'reviewer' | 'observer'

export type FieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'date'
  | 'datetime'
  | 'select'
  | 'multiselect'
  | 'boolean'
  | 'email'
  | 'phone'
  | 'url'

export type CaseQuotaDenialReason =
  | 'no_active_seat'
  | 'case_limit_reached'
  | 'volume_limit_reached'
  | 'license_expired'
  | 'license_inactive'

// =============================================================================
// Core Data Types
// =============================================================================

/** Ad-hoc field attached to a case */
export interface AdHocField {
  id: string
  label: string
  fieldType: FieldType
  value: unknown
  createdAt: string
  createdBy: string
}

/** Case entity from the API */
export interface Case {
  id: string
  organisationId: string
  ownerUserId: string
  licenseId: string
  clientProfileId?: string
  clientName?: string
  clientShortName?: string
  name: string
  orgCaseReference?: string
  scopeDescription: string
  priority: CasePriority
  status: CaseStatus
  libraryFields: Record<string, unknown>
  adHocFields: AdHocField[]
  dataVolumeUsedBytes: number
  dataVolumeLimitBytes?: number
  collectionCount: number
  artifactCount: number
  analysisCompletion: number
  lastActivityAt: string
  createdAt: string
  updatedAt: string
  closedAt?: string
  archivedAt?: string
}

/** Client profile status */
export type ClientProfileStatus = 'active' | 'inactive' | 'archived'

/** Client profile entity */
export interface ClientProfile {
  id: string
  ownerOrganisationId: string
  name: string
  shortName?: string
  industry?: string
  primaryContactName?: string
  primaryContactEmail?: string
  primaryContactPhone?: string
  profileData: Record<string, unknown>
  /** Status: active, inactive, or archived */
  status: ClientProfileStatus
  /** Internal reference code (e.g., CLI-001) */
  referenceCode?: string
  /** Internal notes about the client */
  notes?: string
  createdAt: string
  updatedAt: string
  createdBy?: string
  /** Soft delete timestamp */
  deletedAt?: string
}

/** Client profile with case count for list views */
export interface ClientProfileWithCaseCount {
  /** The client profile data (flattened) */
  id: string
  ownerOrganisationId: string
  name: string
  shortName?: string
  industry?: string
  primaryContactName?: string
  primaryContactEmail?: string
  primaryContactPhone?: string
  profileData: Record<string, unknown>
  status: ClientProfileStatus
  referenceCode?: string
  notes?: string
  createdAt: string
  updatedAt: string
  createdBy?: string
  /** Number of cases linked to this client */
  caseCount: number
  /** Timestamp of most recent linked case */
  lastCaseCreatedAt?: string
}

/** Summary of a linked case */
export interface LinkedCaseSummary {
  id: string
  name: string
  status: string
}

/** Summary of linked cases for delete confirmation */
export interface ClientCaseSummary {
  caseCount: number
  cases: LinkedCaseSummary[]
}

/** Client details response including case summary */
export interface ClientDetailsResponse {
  profile: ClientProfile
  caseSummary: ClientCaseSummary
}

/** Options for deleting a client with linked cases */
export interface DeleteClientOptions {
  /** Client ID to reassign cases to before deletion */
  reassignTo?: string
  /** If true, unlink all cases (set clientProfileId to NULL) */
  unlinkCases?: boolean
}

/** Case field definition (system or org-specific) */
export interface CaseFieldDefinition {
  id: string
  organisationId?: string
  fieldKey: string
  displayName: string
  description?: string
  fieldType: FieldType
  options?: string[]
  validationRules?: Record<string, unknown>
  category: string
  sortOrder: number
  isSystem: boolean
  createdAt: string
}

/** Case team member with user info */
export interface CaseTeamMember {
  id: string
  caseId: string
  userId: string
  userName?: string
  userEmail?: string
  caseRole: CaseTeamRole
  addedAt: string
  addedBy?: string
}

/** Quota status for case creation */
export interface CaseQuotaStatus {
  userId: string
  organisationId: string
  licenseId: string
  casesPerSeatLimit: number
  activeCases: number
  casesRemaining: number
  seatVolumeLimitBytes?: number
  totalVolumeUsedBytes: number
  volumeRemainingBytes?: number
  canCreateCase: boolean
  denialReason?: CaseQuotaDenialReason
}

// =============================================================================
// Input Types
// =============================================================================

/** Input for creating a new case */
export interface CreateCaseInput {
  name: string
  scopeDescription: string
  clientProfileId?: string
  orgCaseReference?: string
  priority?: CasePriority
  libraryFields?: Record<string, unknown>
  adHocFields?: Omit<AdHocField, 'id' | 'createdAt' | 'createdBy'>[]
}

/** Input for updating an existing case */
export interface UpdateCaseInput {
  name?: string
  scopeDescription?: string
  clientProfileId?: string | null
  orgCaseReference?: string | undefined
  priority?: CasePriority
  status?: CaseStatus
  libraryFields?: Record<string, unknown>
  adHocFields?: AdHocField[]
}

/** Input for updating case status only */
export interface UpdateCaseStatusInput {
  status: CaseStatus
}

/** Input for creating a client profile */
export interface CreateClientProfileInput {
  name: string
  shortName?: string
  industry?: string
  /** Internal reference code (e.g., CLI-001) */
  referenceCode?: string
  /** Internal notes about the client */
  notes?: string
  primaryContactName?: string
  primaryContactEmail?: string
  primaryContactPhone?: string
  profileData?: Record<string, unknown>
}

/** Input for updating a client profile */
export interface UpdateClientProfileInput {
  /** Name can only be updated by org_admin+ */
  name?: string
  shortName?: string
  industry?: string
  /** Internal reference code */
  referenceCode?: string
  /** Internal notes */
  notes?: string
  /** Status: active, inactive, or archived */
  status?: ClientProfileStatus
  primaryContactName?: string
  primaryContactEmail?: string
  primaryContactPhone?: string
  profileData?: Record<string, unknown>
}

/** Input for creating a custom field definition */
export interface CreateCaseFieldInput {
  fieldKey: string
  displayName: string
  description?: string
  fieldType: FieldType
  options?: string[]
  validationRules?: Record<string, unknown>
  category: string
  sortOrder?: number
}

/** Input for adding a team member */
export interface AddTeamMemberInput {
  userId: string
  caseRole: CaseTeamRole
}

// =============================================================================
// Query Parameters
// =============================================================================

/** Parameters for listing cases */
export interface ListCasesParams {
  status?: CaseStatus
  priority?: CasePriority
  clientProfileId?: string
  search?: string
  limit?: number
  offset?: number
  [key: string]: string | number | undefined
}

/** Parameters for listing client profiles */
export interface ListClientProfilesParams {
  search?: string
  /** Filter by status (active, inactive, archived) */
  status?: ClientProfileStatus
  limit?: number
  offset?: number
  [key: string]: string | number | undefined
}

/** Parameters for listing field definitions */
export interface ListFieldsParams {
  category?: string
  [key: string]: string | undefined
}

// =============================================================================
// View Models
// =============================================================================

/** Cases grouped by status for Kanban display */
export interface CasesByStatus {
  collection: Case[]
  analysis: Case[]
  review: Case[]
  reporting: Case[]
  closed: Case[]
  archived: Case[]
}

/** Display labels for case status */
export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  collection: 'Collection',
  analysis: 'Analysis',
  review: 'Review',
  reporting: 'Reporting',
  closed: 'Closed',
  archived: 'Archived',
}

/** Display labels for case priority */
export const CASE_PRIORITY_LABELS: Record<CasePriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
}

/** Display labels for team roles */
export const CASE_TEAM_ROLE_LABELS: Record<CaseTeamRole, string> = {
  lead: 'Lead',
  analyst: 'Analyst',
  reviewer: 'Reviewer',
  observer: 'Observer',
}

/** Display labels for client profile status */
export const CLIENT_PROFILE_STATUS_LABELS: Record<ClientProfileStatus, string> = {
  active: 'Active',
  inactive: 'Inactive',
  archived: 'Archived',
}

/** Field categories */
export const FIELD_CATEGORIES = [
  'location',
  'scope',
  'classification',
  'timeline',
  'compliance',
  'reference',
  'custom',
] as const

export type FieldCategory = (typeof FIELD_CATEGORIES)[number]

// =============================================================================
// Component Props (for UI components)
// =============================================================================

export interface CaseManagementDashboardProps {
  /** All cases to display in the Kanban board */
  cases: Case[]
  /** Available client profiles for the new case form */
  clients: ClientProfile[]
  /** Current quota status */
  quotaStatus?: CaseQuotaStatus
  /** Loading state */
  isLoading?: boolean
  /** Called when user clicks on a case card to open it */
  onOpenCase?: (caseId: string) => void
  /** Called when user submits the new case form */
  onCreateCase?: (data: CreateCaseInput) => void
  /** Called when user wants to start a preview (opens import dialog) */
  onStartPreview?: () => void
  /** Called when user drags a case to a new status column */
  onUpdateCaseStatus?: (caseId: string, newStatus: CaseStatus) => void
}

export interface CaseCardProps {
  /** The case to display */
  caseData: Case
  /** Called when the card is clicked */
  onClick?: (() => void) | undefined
  /** Called when case is dragged to a new column */
  onDragEnd?: ((newStatus: CaseStatus) => void) | undefined
}

export interface NewCaseModalProps {
  /** Whether the modal is open */
  isOpen: boolean
  /** Available client profiles for selection */
  clients: ClientProfile[]
  /** Available field definitions */
  fieldDefinitions: CaseFieldDefinition[]
  /** Current quota status */
  quotaStatus?: CaseQuotaStatus
  /** Called when the form is submitted */
  onSubmit?: (data: CreateCaseInput) => void
  /** Called when the modal is closed/cancelled */
  onClose?: () => void
}

export interface KanbanColumnProps {
  /** The status this column represents */
  status: CaseStatus
  /** Column display title */
  title: string
  /** Cases in this column */
  cases: Case[]
  /** Called when a case card is clicked */
  onOpenCase?: ((caseId: string) => void) | undefined
  /** Called when a case is dropped into this column */
  onDropCase?: ((caseId: string) => void) | undefined
}

export interface TeamMemberListProps {
  /** Team members to display */
  members: CaseTeamMember[]
  /** Whether the user can manage team */
  canManage?: boolean
  /** Called when removing a member */
  onRemove?: (userId: string) => void
  /** Called when adding a member */
  onAdd?: (userId: string, role: CaseTeamRole) => void
}

// =============================================================================
// Legacy Types (kept for backwards compatibility)
// =============================================================================

/** @deprecated Use ClientProfile instead */
export interface Client {
  id: string
  name: string
  industry: string
}

/** @deprecated Use CreateCaseInput instead */
export interface NewCaseFormData {
  name: string
  clientName: string
  description: string
  priority: CasePriority
}

export interface ArtifactType {
  id: string
  name: string
  category: string
}

export interface ArtifactTypeSummary {
  typeId: string
  typeName: string
  count: number
  flaggedCount: number
}

export interface PreviewSummary {
  collectionName: string
  endpointName: string
  collectedAt: string
  totalArtifacts: number
  artifactsByType: ArtifactTypeSummary[]
  totalFlagged: number
}

export interface PreviewImportProps {
  /** Whether the import dialog is open */
  isOpen: boolean
  /** Called when a file is selected for upload */
  onFileUpload?: (file: File) => void
  /** Called when a storage path is selected */
  onStorageImport?: (path: string) => void
  /** Called when the dialog is closed */
  onClose?: () => void
}

export interface PreviewViewProps {
  /** The preview summary data */
  summary: PreviewSummary
  /** Artifact types for reference */
  artifactTypes: ArtifactType[]
  /** Called when user expands an artifact type section */
  onExpandType?: (typeId: string) => void
  /** Called when user decides to save preview as a new case */
  onSaveAsCase?: () => void
  /** Called when user discards the preview */
  onDiscard?: () => void
}
